import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import RotateLeftRoundedIcon from '@mui/icons-material/RotateLeftRounded';
import RotateRightRoundedIcon from '@mui/icons-material/RotateRightRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

import { MOTION, ellipsis } from './chatTheme';

/**
 * Просмотрщик изображений.
 *
 * Раньше клик по фотографии в чате открывал её в новой вкладке браузера:
 * человек терял место в переписке, а вернувшись — снова искал нужное
 * сообщение. Здесь картинка открывается поверх ленты: масштаб, поворот,
 * перетаскивание, скачивание и перелистывание соседних фото из того же
 * сообщения.
 *
 * Рисуется порталом в body — иначе любой родитель с overflow обрезал бы окно.
 *
 * Управление: ← → листают, + − масштабируют, R поворачивает, 0 сбрасывает,
 * Esc закрывает. Колесо мыши — масштаб, перетаскивание — сдвиг.
 */

const ZOOM_MIN = 0.2;
const ZOOM_MAX = 8;
const ZOOM_STEP = 0.25;

const clampZoom = (v) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v));

export default function ImageViewer({ images = [], startIndex = 0, onClose }) {
  const [index, setIndex] = useState(startIndex);
  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [loaded, setLoaded] = useState(false);
  const [mediaError, setMediaError] = useState('');
  const [resolved, setResolved] = useState({});
  const [attempts, setAttempts] = useState({});
  const [versions, setVersions] = useState({});
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const dragRef = useRef(null);
  const surfaceRef = useRef(null);

  const original = images[index] || null;
  const current = original ? { ...original, url: resolved[index] || original.url } : null;
  const many = images.length > 1;

  const reset = useCallback(() => {
    setZoom(1);
    setRotate(0);
    setOffset({ x: 0, y: 0 });
  }, []);

  const go = useCallback((delta) => {
    if (images.length < 2) return;
    setIndex((i) => (i + delta + images.length) % images.length);
    reset();
    setLoaded(false); setMediaError('');
  }, [images.length, reset]);

  // Клавиатура. Слушаем на document: фокус может быть где угодно.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose?.(); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); return; }
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); return; }
      if (e.key === '+' || e.key === '=') { e.preventDefault(); setZoom((z) => clampZoom(z + ZOOM_STEP)); return; }
      if (e.key === '-' || e.key === '_') { e.preventDefault(); setZoom((z) => clampZoom(z - ZOOM_STEP)); return; }
      if (e.key === '0') { e.preventDefault(); reset(); return; }
      if (e.key === 'r' || e.key === 'R' || e.key === 'к' || e.key === 'К') {
        e.preventDefault();
        setRotate((r) => (r + (e.shiftKey ? -90 : 90)) % 360);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [go, onClose, reset]);

  // Пока окно открыто, страница под ним не должна прокручиваться.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Колесо мыши масштабирует. passive:false — иначе preventDefault не сработает
  // и страница под окном поедет вместе с масштабом.
  useEffect(() => {
    const el = surfaceRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      setZoom((z) => clampZoom(z - Math.sign(e.deltaY) * ZOOM_STEP));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const startDrag = (e) => {
    const p = e.touches?.[0] || e;
    dragRef.current = { x: p.clientX, y: p.clientY, ox: offset.x, oy: offset.y };
  };
  const moveDrag = (e) => {
    if (!dragRef.current) return;
    const p = e.touches?.[0] || e;
    setOffset({
      x: dragRef.current.ox + (p.clientX - dragRef.current.x),
      y: dragRef.current.oy + (p.clientY - dragRef.current.y),
    });
  };
  const endDrag = () => { dragRef.current = null; };

  const refresh = async () => {
    const entry = images[index];
    if (!entry?.refresh) { setMediaError('Не удалось открыть фото. Закройте просмотр и повторите попытку.'); return; }
    setAttempts(prev => ({ ...prev, [index]: true }));
    setMediaError(''); setLoaded(false);
    try {
      const fresh = await entry.refresh();
      if (alive.current) {
        setResolved(prev => ({ ...prev, [index]: fresh.url }));
        setVersions(prev => ({ ...prev, [index]: (prev[index] || 0) + 1 }));
      }
    } catch (error) { if (alive.current) setMediaError(error.message || 'Не удалось загрузить фото'); }
  };
  const failed = () => {
    if (!attempts[index] && original?.refresh) refresh();
    else setMediaError('Не удалось загрузить фото. Повторите попытку или скачайте оригинал.');
  };
  const download = async () => {
    if (!current) return;
    const tab = window.open('about:blank', '_blank');
    if (!tab) { setMediaError('Разрешите открытие новой вкладки для скачивания'); return; }
    tab.opener = null;
    try {
      const fresh = original.refresh ? await original.refresh() : current;
      tab.location.replace(fresh.url);
    } catch (error) { tab.close(); setMediaError(error.message || 'Не удалось скачать файл'); }
  };

  if (!current) return null;

  const toolButton = {
    '--IconButton-size': '36px',
    borderRadius: '50%',
    color: 'rgba(255,255,255,0.88)',
    '--Icon-color': 'rgba(255,255,255,0.88)',
    '& svg': { color: 'rgba(255,255,255,0.88)' },
    backgroundColor: 'rgba(255,255,255,0.08)',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' },
  };

  return createPortal(
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: MOTION.fast }}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 13000,
        backgroundColor: 'rgba(8,11,20,0.92)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Шапка */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 2, py: 1.25, flexShrink: 0,
          color: 'rgba(255,255,255,0.9)',
        }}
      >
        <Typography
          level="body-sm"
          sx={{ ...ellipsis, flex: 1, color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}
        >
          {current.name || 'Изображение'}
        </Typography>
        {many && (
          <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.6)', flexShrink: 0 }}>
            {index + 1} из {images.length}
          </Typography>
        )}
        <IconButton size="sm" variant="plain" onClick={onClose} sx={toolButton} title="Закрыть (Esc)">
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      {/* Полотно */}
      <Box
        ref={surfaceRef}
        onMouseDown={startDrag}
        onMouseMove={moveDrag}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onTouchStart={startDrag}
        onTouchMove={moveDrag}
        onTouchEnd={endDrag}
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        sx={{
          flex: 1, minHeight: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
          cursor: zoom > 1 ? 'grab' : 'default',
          userSelect: 'none',
          position: 'relative',
        }}
      >
        <AnimatePresence mode="wait">
          <Box
            key={`${current.url || index}:${versions[index] || 0}`}
            component={motion.img}
            src={current.url}
            alt={current.name || ''}
            draggable={false}
            onLoad={() => { setLoaded(true); setMediaError(''); }}
            onError={failed}
            crossOrigin="anonymous" referrerPolicy="no-referrer"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: loaded ? 1 : 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: MOTION.base, ease: MOTION.ease }}
            style={{
              maxWidth: '92%',
              maxHeight: '100%',
              objectFit: 'contain',
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom}) rotate(${rotate}deg)`,
              transition: dragRef.current ? 'none' : 'transform .18s ease-out',
              willChange: 'transform',
            }}
          />
        </AnimatePresence>

        {mediaError && <Box sx={{ p: 3, maxWidth: 380, textAlign: 'center', position: 'absolute' }}>
          <Typography sx={{ color: '#fff', mb: 2 }}>{mediaError}</Typography>
          <Button variant="soft" onClick={refresh}>Повторить</Button>
        </Box>}
        {many && (
          <>
            <IconButton
              size="lg"
              variant="plain"
              onClick={(e) => { e.stopPropagation(); go(-1); }}
              sx={{ ...toolButton, position: 'absolute', left: 12, '--IconButton-size': '44px' }}
              title="Предыдущее (←)"
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
            <IconButton
              size="lg"
              variant="plain"
              onClick={(e) => { e.stopPropagation(); go(1); }}
              sx={{ ...toolButton, position: 'absolute', right: 12, '--IconButton-size': '44px' }}
              title="Следующее (→)"
            >
              <ChevronRightRoundedIcon />
            </IconButton>
          </>
        )}
      </Box>

      {/* Панель инструментов */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: MOTION.base, ease: MOTION.ease }}
        sx={{
          flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 0.5, py: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            px: 1, py: 0.75, borderRadius: '999px',
            backgroundColor: 'rgba(20,26,42,0.85)',
            border: '1px solid rgba(255,255,255,0.10)',
            boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
          }}
        >
          <IconButton size="sm" variant="plain" sx={toolButton} title="Уменьшить (−)"
                      onClick={() => setZoom((z) => clampZoom(z - ZOOM_STEP))}>
            <RemoveRoundedIcon />
          </IconButton>
          <Typography
            level="body-xs"
            sx={{ width: 52, textAlign: 'center', color: 'rgba(255,255,255,0.85)', fontWeight: 700 }}
          >
            {Math.round(zoom * 100)}%
          </Typography>
          <IconButton size="sm" variant="plain" sx={toolButton} title="Увеличить (+)"
                      onClick={() => setZoom((z) => clampZoom(z + ZOOM_STEP))}>
            <AddRoundedIcon />
          </IconButton>

          <Box sx={{ width: '1px', height: 22, mx: 0.5, backgroundColor: 'rgba(255,255,255,0.14)' }} />

          <IconButton size="sm" variant="plain" sx={toolButton} title="Повернуть влево (Shift+R)"
                      onClick={() => setRotate((r) => (r - 90) % 360)}>
            <RotateLeftRoundedIcon />
          </IconButton>
          <IconButton size="sm" variant="plain" sx={toolButton} title="Повернуть вправо (R)"
                      onClick={() => setRotate((r) => (r + 90) % 360)}>
            <RotateRightRoundedIcon />
          </IconButton>
          <IconButton size="sm" variant="plain" sx={toolButton} title="Сбросить (0)" onClick={reset}>
            <RestartAltRoundedIcon />
          </IconButton>

          <Box sx={{ width: '1px', height: 22, mx: 0.5, backgroundColor: 'rgba(255,255,255,0.14)' }} />

          <IconButton size="sm" variant="plain" sx={toolButton} title="Скачать" onClick={download}>
            <DownloadRoundedIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>,
    document.body,
  );
}
